import { z } from "zod";

import {
  BILLING_CURRENCY,
  fixedTermsDiscountFits,
  MAX_AMOUNT_MINOR,
  MAX_DISCOUNT_BASIS_POINTS,
  MAX_LINE_QUANTITY,
  type BillingDiscount,
} from "./billing-money";
import { OpaqueIdSchema } from "./identity-schemas";

/**
 * Billing terms and their collection (ADR-0073).
 *
 * One module for the whole surface, for the reason `support-access-schemas.ts` gives: the form an
 * employee fills in, the projection the console renders, the row the repository writes, and the
 * amount handed to Stripe all parse the same shapes, so a total on screen cannot differ from the
 * total collected.
 */

export const MinorAmountSchema = z.number().int().min(0).max(MAX_AMOUNT_MINOR);
export const PositiveMinorAmountSchema = MinorAmountSchema.min(1);
export const BillingCurrencySchema = z.literal(BILLING_CURRENCY);

/** Stripe identifiers: a lowercase prefix, an underscore, then mixed-case alphanumerics. */
export const StripeIdSchema = z
  .string()
  .min(3)
  .max(255)
  .regex(/^[a-z]+_[A-Za-z0-9]+$/, "Expected a Stripe identifier");

/** Free text an employee wrote for another employee. Bounded, never rendered to a customer. */
export const BillingReasonSchema = z.string().trim().min(3).max(500);
export const BillingNotesSchema = z.string().trim().max(4000);
export const BillingDescriptionSchema = z.string().trim().min(2).max(200);
export const BillingTitleSchema = z.string().trim().min(2).max(160);

export const BillingCadenceSchema = z.enum(["month", "year"]);
export type BillingCadence = z.infer<typeof BillingCadenceSchema>;

export const BillingLineKindSchema = z.enum(["one_time", "recurring", "credit"]);
export type BillingLineKind = z.infer<typeof BillingLineKindSchema>;

export const BillingDiscountSchema: z.ZodType<BillingDiscount> = z.discriminatedUnion("type", [
  z.strictObject({
    type: z.literal("percent"),
    basisPoints: z.number().int().min(1).max(MAX_DISCOUNT_BASIS_POINTS),
  }),
  z.strictObject({ type: z.literal("fixed"), amountMinor: PositiveMinorAmountSchema }),
]);

/**
 * One line of a terms version, as an employee enters it.
 *
 * `cadence` is present exactly when the line recurs, which the database CHECK also enforces. A
 * fixed line discount cannot exceed the line, so a line never nets negative; a credit line is the
 * only way to put money on the customer's side of the ledger, and it takes no discount.
 */
export const BillingLineInputSchema = z
  .strictObject({
    kind: BillingLineKindSchema,
    description: BillingDescriptionSchema,
    unitAmountMinor: MinorAmountSchema,
    quantity: z.number().int().min(1).max(MAX_LINE_QUANTITY),
    cadence: BillingCadenceSchema.nullable(),
    discount: BillingDiscountSchema.nullable(),
  })
  .superRefine((line, context) => {
    if ((line.kind === "recurring") !== (line.cadence !== null)) {
      context.addIssue({
        code: "custom",
        path: ["cadence"],
        message: "Recurring lines name a cadence; one-time and credit lines do not.",
      });
    }
    if (line.kind === "credit" && line.discount !== null) {
      context.addIssue({
        code: "custom",
        path: ["discount"],
        message: "A credit is not discounted.",
      });
    }
    if (
      line.discount?.type === "fixed" &&
      line.discount.amountMinor > line.unitAmountMinor * line.quantity
    ) {
      context.addIssue({
        code: "custom",
        path: ["discount"],
        message: "A fixed discount cannot exceed the line it comes off.",
      });
    }
  });
export type BillingLineInput = z.infer<typeof BillingLineInputSchema>;

/**
 * A whole terms version, as drafted or edited. `reason` is the change-log entry and is mandatory:
 * every write to commercial terms records why, and a form that lets the field be skipped is a
 * change log with holes.
 */
const BillingTermsFieldsSchema = z.strictObject({
  toolId: OpaqueIdSchema.nullable(),
  title: BillingTitleSchema,
  effectiveAt: z.number().int().positive(),
  endsAt: z.number().int().positive().nullable(),
  notes: BillingNotesSchema.nullable(),
  discount: BillingDiscountSchema.nullable(),
  lines: z.array(BillingLineInputSchema).min(1).max(50),
  reason: BillingReasonSchema,
});

function refineTerms(
  terms: z.infer<typeof BillingTermsFieldsSchema>,
  context: z.RefinementCtx,
): void {
  if (terms.endsAt !== null && terms.endsAt < terms.effectiveAt) {
    context.addIssue({
      code: "custom",
      path: ["endsAt"],
      message: "Terms cannot end before they take effect.",
    });
  }
  if (!fixedTermsDiscountFits(terms.lines, terms.discount)) {
    context.addIssue({
      code: "custom",
      path: ["discount"],
      message: "A fixed terms discount comes off the one-time charges and cannot exceed them.",
    });
  }
}

/** Editing a draft. Which approved version it revises is fixed at creation. */
export const BillingTermsUpdateSchema = BillingTermsFieldsSchema.superRefine(refineTerms);
export type BillingTermsUpdate = z.infer<typeof BillingTermsUpdateSchema>;

export const BillingTermsInputSchema = BillingTermsFieldsSchema.extend({
  /** The approved version this draft revises. Approving the draft supersedes it. */
  supersedesTermsId: OpaqueIdSchema.nullable(),
}).superRefine(refineTerms);
export type BillingTermsInput = z.infer<typeof BillingTermsInputSchema>;

/**
 * `superseded`, `ended`, and `canceled` are three terminal states, not one, because they mean
 * different things to whoever reads the history: replaced by a newer version, ran its course, or
 * fell through. None of them deletes anything.
 */
export const BillingTermsStatusSchema = z.enum([
  "draft",
  "approved",
  "superseded",
  "ended",
  "canceled",
]);
export type BillingTermsStatus = z.infer<typeof BillingTermsStatusSchema>;

export const BillingTransitionSchema = z.strictObject({ reason: BillingReasonSchema });
export const BillingEndSchema = z.strictObject({
  reason: BillingReasonSchema,
  /** When the terms stop applying. Defaults to now; may be later, never earlier. */
  endsAt: z.number().int().positive().nullable().default(null),
});

const BillingPersonSchema = z.strictObject({ displayName: z.string().min(1).max(120) });

export const BillingToolSchema = z.strictObject({
  id: OpaqueIdSchema,
  displayName: z.string().min(1).max(120),
});
export type BillingTool = z.infer<typeof BillingToolSchema>;

export const BillingLineSchema = z.strictObject({
  id: OpaqueIdSchema,
  position: z.number().int().min(0),
  kind: BillingLineKindSchema,
  description: BillingDescriptionSchema,
  unitAmountMinor: MinorAmountSchema,
  quantity: z.number().int().min(1).max(MAX_LINE_QUANTITY),
  cadence: BillingCadenceSchema.nullable(),
  discount: BillingDiscountSchema.nullable(),
  /** Unit times quantity less the line discount, computed once on the server. */
  totalMinor: MinorAmountSchema,
});
export type BillingLine = z.infer<typeof BillingLineSchema>;

export const BillingTotalsSchema = z.strictObject({
  oneTimeMinor: MinorAmountSchema,
  monthlyMinor: MinorAmountSchema,
  annualMinor: MinorAmountSchema,
  creditMinor: MinorAmountSchema,
});
export type BillingTotals = z.infer<typeof BillingTotalsSchema>;

export const BillingTermsSchema = z.strictObject({
  id: OpaqueIdSchema,
  organizationId: OpaqueIdSchema,
  /** Present when the terms are for one tool; null for organization-level terms. */
  tool: BillingToolSchema.nullable(),
  /** The accepted quote this version came from (ADR-0075); null for terms drafted by hand. */
  quoteId: OpaqueIdSchema.nullable(),
  title: BillingTitleSchema,
  currency: BillingCurrencySchema,
  status: BillingTermsStatusSchema,
  effectiveAt: z.number().int().positive(),
  endsAt: z.number().int().positive().nullable(),
  notes: BillingNotesSchema.nullable(),
  discount: BillingDiscountSchema.nullable(),
  lines: z.array(BillingLineSchema).max(50),
  totals: BillingTotalsSchema,
  supersedesTermsId: OpaqueIdSchema.nullable(),
  supersededByTermsId: OpaqueIdSchema.nullable(),
  createdAt: z.number().int().positive(),
  createdBy: BillingPersonSchema.nullable(),
  updatedAt: z.number().int().positive(),
  approvedAt: z.number().int().positive().nullable(),
  approvedBy: BillingPersonSchema.nullable(),
  closedAt: z.number().int().positive().nullable(),
  closedBy: BillingPersonSchema.nullable(),
});
export type BillingTerms = z.infer<typeof BillingTermsSchema>;

export const BillingTermsResponseSchema = z.strictObject({ terms: BillingTermsSchema });

/** One entry of the commercial change log, read back from the audit trail. */
export const BillingHistoryEventSchema = z.strictObject({
  id: OpaqueIdSchema,
  event: z.string().min(1).max(100),
  subjectId: z.string().min(1).max(128).nullable(),
  outcome: z.enum(["success", "denied", "error"]),
  actor: BillingPersonSchema.nullable(),
  reason: z.string().max(500).nullable(),
  detail: z.record(z.string(), z.union([z.string(), z.number(), z.boolean(), z.null()])),
  createdAt: z.number().int().positive(),
});
export type BillingHistoryEvent = z.infer<typeof BillingHistoryEventSchema>;

export const BillingHistoryResponseSchema = z.strictObject({
  events: z.array(BillingHistoryEventSchema).max(100),
  nextCursor: z.string().max(1024).nullable(),
});

// ---------------------------------------------------------------------------
// Quotes, accepted by the customer (ADR-0075)
// ---------------------------------------------------------------------------

/**
 * A quote is a terms version proposed to the customer. It carries everything a version does plus
 * what the customer needs to decide: a message written for them, a validity window, and the
 * billing contacts they named. `expired` is derived from `validUntil` on every read and never
 * stored; a customer cannot accept an expired quote.
 */
export const BillingQuoteStatusSchema = z.enum([
  "draft",
  "sent",
  "accepted",
  "declined",
  "expired",
  "superseded",
  "canceled",
]);
export type BillingQuoteStatus = z.infer<typeof BillingQuoteStatusSchema>;

/** Free text written for the customer. Shown on their Billing tab and in the proposal email. */
export const BillingMessageSchema = z.string().trim().max(2000);

export const BillingQuoteContactSchema = z.strictObject({
  name: z.string().trim().min(1).max(120),
  email: z.email().max(320),
});
export type BillingQuoteContact = z.infer<typeof BillingQuoteContactSchema>;

const BillingQuoteFieldsSchema = BillingTermsFieldsSchema.extend({
  message: BillingMessageSchema.nullable(),
  /** Whom the customer named for billing correspondence. Informational; up to five. */
  contacts: z.array(BillingQuoteContactSchema).max(5),
});

/** Editing a draft quote. Which sent quote it replaces is fixed at creation. */
export const BillingQuoteUpdateSchema = BillingQuoteFieldsSchema.superRefine(refineTerms);
export type BillingQuoteUpdate = z.infer<typeof BillingQuoteUpdateSchema>;

export const BillingQuoteInputSchema = BillingQuoteFieldsSchema.extend({
  /** The sent quote this one replaces. Sending this quote moves that one to `superseded`. */
  supersedesQuoteId: OpaqueIdSchema.nullable(),
}).superRefine(refineTerms);
export type BillingQuoteInput = z.infer<typeof BillingQuoteInputSchema>;

export const BillingQuoteSendSchema = z.strictObject({
  /** The last second the customer may accept. The console sends the end of the chosen day. */
  validUntil: z.number().int().positive(),
  reason: BillingReasonSchema,
});
export type BillingQuoteSend = z.infer<typeof BillingQuoteSendSchema>;

/** The customer's answer. The reason is theirs to give or skip; employees read it in History. */
export const BillingQuoteDecisionSchema = z.strictObject({
  decision: z.enum(["accept", "decline"]),
  reason: z.string().trim().max(500).nullable().default(null),
});
export type BillingQuoteDecision = z.infer<typeof BillingQuoteDecisionSchema>;

export const BillingQuoteSchema = z.strictObject({
  id: OpaqueIdSchema,
  organizationId: OpaqueIdSchema,
  tool: BillingToolSchema.nullable(),
  title: BillingTitleSchema,
  currency: BillingCurrencySchema,
  status: BillingQuoteStatusSchema,
  effectiveAt: z.number().int().positive(),
  endsAt: z.number().int().positive().nullable(),
  validUntil: z.number().int().positive().nullable(),
  message: BillingMessageSchema.nullable(),
  notes: BillingNotesSchema.nullable(),
  discount: BillingDiscountSchema.nullable(),
  lines: z.array(BillingLineSchema).max(50),
  totals: BillingTotalsSchema,
  contacts: z.array(BillingQuoteContactSchema).max(5),
  /** The active terms version acceptance supersedes; fixed when the quote is sent. */
  supersedesTermsId: OpaqueIdSchema.nullable(),
  supersedesQuoteId: OpaqueIdSchema.nullable(),
  supersededByQuoteId: OpaqueIdSchema.nullable(),
  /** The terms version acceptance created. Present exactly when accepted. */
  termsId: OpaqueIdSchema.nullable(),
  createdAt: z.number().int().positive(),
  createdBy: BillingPersonSchema.nullable(),
  updatedAt: z.number().int().positive(),
  sentAt: z.number().int().positive().nullable(),
  sentBy: BillingPersonSchema.nullable(),
  decidedAt: z.number().int().positive().nullable(),
  /** The customer administrator who accepted or declined. */
  decidedBy: BillingPersonSchema.nullable(),
  decisionReason: z.string().max(500).nullable(),
  closedAt: z.number().int().positive().nullable(),
  closedBy: BillingPersonSchema.nullable(),
});
export type BillingQuote = z.infer<typeof BillingQuoteSchema>;

export const BillingQuoteResponseSchema = z.strictObject({ quote: BillingQuoteSchema });

// ---------------------------------------------------------------------------
// Collection: Stripe and manual records
// ---------------------------------------------------------------------------

export const BillingSourceSchema = z.enum(["stripe", "manual", "external"]);
export type BillingSource = z.infer<typeof BillingSourceSchema>;

export const BillingCustomerSchema = z.strictObject({
  organizationId: OpaqueIdSchema,
  stripeCustomerId: StripeIdSchema,
  billingEmail: z.string().max(320).nullable(),
  createdAt: z.number().int().positive(),
  createdBy: BillingPersonSchema.nullable(),
});
export type BillingCustomer = z.infer<typeof BillingCustomerSchema>;

export const BillingCustomerLinkSchema = z.strictObject({
  billingEmail: z.email().max(320).nullable().default(null),
  reason: BillingReasonSchema,
});

/**
 * `issuing` is the window between our row and Stripe's: the row exists, the idempotency key is
 * fixed, and the Stripe invoice may or may not have been created yet. Recovery is re-issuing with
 * the same key, which is why the state is visible rather than hidden behind a retry.
 *
 * `processing` is the window between the customer paying and the money arriving: a bank debit
 * takes up to four business days to settle or fail. Stripe keeps the invoice open meanwhile;
 * this platform shows it as processing so that nobody chases, and nobody pays twice.
 */
export const BillingInvoiceStatusSchema = z.enum([
  "issuing",
  "open",
  "processing",
  "past_due",
  "paid",
  "void",
  "uncollectible",
]);
export type BillingInvoiceStatus = z.infer<typeof BillingInvoiceStatusSchema>;

export const BillingInvoiceSchema = z.strictObject({
  id: OpaqueIdSchema,
  organizationId: OpaqueIdSchema,
  tool: BillingToolSchema.nullable(),
  termsId: OpaqueIdSchema,
  termsLineId: OpaqueIdSchema.nullable(),
  subscriptionId: OpaqueIdSchema.nullable(),
  source: BillingSourceSchema,
  stripeInvoiceId: StripeIdSchema.nullable(),
  externalReference: z.string().max(120).nullable(),
  description: BillingDescriptionSchema,
  currency: BillingCurrencySchema,
  amountDueMinor: MinorAmountSchema,
  amountPaidMinor: MinorAmountSchema,
  amountRefundedMinor: MinorAmountSchema,
  status: BillingInvoiceStatusSchema,
  issuedAt: z.number().int().positive(),
  dueAt: z.number().int().positive().nullable(),
  paidAt: z.number().int().positive().nullable(),
  /** When the payment now in flight was first seen; present exactly when the status is `processing`. */
  paymentPendingAt: z.number().int().positive().nullable(),
  /** How money that did not move through Stripe arrived: a check, a wire. */
  paymentMethod: z.enum(["check", "wire", "ach", "other"]).nullable(),
  hostedInvoiceUrl: z.string().max(2048).nullable(),
  invoiceNumber: z.string().max(80).nullable(),
  lastPaymentError: z.string().max(300).nullable(),
  createdBy: BillingPersonSchema.nullable(),
  createdAt: z.number().int().positive(),
  updatedAt: z.number().int().positive(),
});
export type BillingInvoice = z.infer<typeof BillingInvoiceSchema>;

export const BillingSubscriptionStatusSchema = z.enum([
  "creating",
  "incomplete",
  "active",
  "past_due",
  "unpaid",
  "paused",
  "canceled",
]);
export type BillingSubscriptionStatus = z.infer<typeof BillingSubscriptionStatusSchema>;

export const BillingSubscriptionSchema = z.strictObject({
  id: OpaqueIdSchema,
  organizationId: OpaqueIdSchema,
  tool: BillingToolSchema.nullable(),
  termsId: OpaqueIdSchema,
  termsLineId: OpaqueIdSchema.nullable(),
  source: BillingSourceSchema,
  stripeSubscriptionId: StripeIdSchema.nullable(),
  externalReference: z.string().max(120).nullable(),
  description: BillingDescriptionSchema,
  currency: BillingCurrencySchema,
  amountMinor: MinorAmountSchema,
  cadence: BillingCadenceSchema,
  status: BillingSubscriptionStatusSchema,
  cancelAtPeriodEnd: z.boolean(),
  currentPeriodStart: z.number().int().positive().nullable(),
  currentPeriodEnd: z.number().int().positive().nullable(),
  startedAt: z.number().int().positive(),
  canceledAt: z.number().int().positive().nullable(),
  createdBy: BillingPersonSchema.nullable(),
  createdAt: z.number().int().positive(),
  updatedAt: z.number().int().positive(),
});
export type BillingSubscription = z.infer<typeof BillingSubscriptionSchema>;

export const BillingRefundStatusSchema = z.enum(["pending", "succeeded", "failed", "canceled"]);

export const BillingRefundSchema = z.strictObject({
  id: OpaqueIdSchema,
  invoiceId: OpaqueIdSchema.nullable(),
  organizationId: OpaqueIdSchema.nullable(),
  source: BillingSourceSchema,
  stripeRefundId: StripeIdSchema.nullable(),
  amountMinor: PositiveMinorAmountSchema,
  status: BillingRefundStatusSchema,
  reason: z.string().max(500).nullable(),
  createdBy: BillingPersonSchema.nullable(),
  createdAt: z.number().int().positive(),
  updatedAt: z.number().int().positive(),
});
export type BillingRefund = z.infer<typeof BillingRefundSchema>;

export const BillingCreditGrantSchema = z.strictObject({
  id: OpaqueIdSchema,
  organizationId: OpaqueIdSchema,
  termsId: OpaqueIdSchema,
  termsLineId: OpaqueIdSchema,
  source: BillingSourceSchema,
  stripeBalanceTransactionId: StripeIdSchema.nullable(),
  amountMinor: PositiveMinorAmountSchema,
  createdBy: BillingPersonSchema.nullable(),
  createdAt: z.number().int().positive(),
});
export type BillingCreditGrant = z.infer<typeof BillingCreditGrantSchema>;

export const BillingOverviewSchema = z.strictObject({
  organizationId: OpaqueIdSchema,
  /** Whether this deployment has Stripe credentials at all. Drives which buttons render. */
  stripeConfigured: z.boolean(),
  /** The configured key's mode; production refuses a test key, and this says why. */
  stripeMode: z.enum(["live", "test"]).nullable(),
  customer: BillingCustomerSchema.nullable(),
  /** The customer's tools, so terms can be attached to one without a second request. */
  tools: z.array(BillingToolSchema).max(200),
  terms: z.array(BillingTermsSchema).max(200),
  quotes: z.array(BillingQuoteSchema).max(200),
  invoices: z.array(BillingInvoiceSchema).max(500),
  subscriptions: z.array(BillingSubscriptionSchema).max(200),
  refunds: z.array(BillingRefundSchema).max(500),
  credits: z.array(BillingCreditGrantSchema).max(200),
  /** Open, past-due, and unpaid amounts, for the badge at the top of the page. */
  outstandingMinor: MinorAmountSchema,
  pastDueCount: z.number().int().min(0),
});
export type BillingOverview = z.infer<typeof BillingOverviewSchema>;

export const BillingOverviewResponseSchema = z.strictObject({ billing: BillingOverviewSchema });

/** Collect one one-time line, or every one-time line of a terms version, on one Stripe invoice. */
export const BillingStripeInvoiceCreateSchema = z.strictObject({
  termsId: OpaqueIdSchema,
  termsLineId: OpaqueIdSchema.nullable().default(null),
  daysUntilDue: z.number().int().min(1).max(180).default(30),
  allowCard: z.boolean().default(false),
  reason: BillingReasonSchema,
});
export type BillingStripeInvoiceCreate = z.infer<typeof BillingStripeInvoiceCreateSchema>;

export const BillingStripeSubscriptionCreateSchema = z.strictObject({
  termsId: OpaqueIdSchema,
  termsLineId: OpaqueIdSchema,
  /** The go-live day billing starts on; null starts it, and sends its first invoice, today. */
  firstInvoiceAt: z.number().int().positive().nullable().default(null),
  daysUntilDue: z.number().int().min(1).max(180).default(30),
  /** ACH (bank debit) is always offered; a card, which costs far more, only when this is set. */
  allowCard: z.boolean().default(false),
  reason: BillingReasonSchema,
});
export type BillingStripeSubscriptionCreate = z.infer<typeof BillingStripeSubscriptionCreateSchema>;

/**
 * Recording money collected outside Stripe: a bank transfer against an emailed PDF, a cheque, a
 * partner's invoicing system. No Stripe object is created or implied, and `source` says which.
 */
export const BillingManualInvoiceCreateSchema = z
  .strictObject({
    termsId: OpaqueIdSchema,
    termsLineId: OpaqueIdSchema.nullable().default(null),
    source: z.enum(["manual", "external"]),
    description: BillingDescriptionSchema,
    amountDueMinor: PositiveMinorAmountSchema,
    externalReference: z.string().trim().max(120).nullable().default(null),
    issuedAt: z.number().int().positive(),
    dueAt: z.number().int().positive().nullable().default(null),
    paidAt: z.number().int().positive().nullable().default(null),
    reason: BillingReasonSchema,
  })
  .refine((invoice) => invoice.dueAt === null || invoice.dueAt >= invoice.issuedAt, {
    path: ["dueAt"],
    message: "An invoice is due on or after the day it was issued.",
  });
export type BillingManualInvoiceCreate = z.infer<typeof BillingManualInvoiceCreateSchema>;

export const BillingManualSubscriptionCreateSchema = z.strictObject({
  termsId: OpaqueIdSchema,
  termsLineId: OpaqueIdSchema,
  source: z.enum(["manual", "external"]),
  externalReference: z.string().trim().max(120).nullable().default(null),
  startedAt: z.number().int().positive(),
  reason: BillingReasonSchema,
});
export type BillingManualSubscriptionCreate = z.infer<typeof BillingManualSubscriptionCreateSchema>;

export const BillingInvoicePaymentRecordSchema = z.strictObject({
  paidAt: z.number().int().positive(),
  method: z.enum(["check", "wire", "ach", "other"]).nullable().default(null),
  reason: BillingReasonSchema,
});

export const BillingInvoiceStatusChangeSchema = z.strictObject({
  status: z.enum(["past_due", "void", "uncollectible"]),
  reason: BillingReasonSchema,
});

export const BillingSubscriptionCancelSchema = z.strictObject({
  atPeriodEnd: z.boolean().default(true),
  reason: BillingReasonSchema,
});
export type BillingSubscriptionCancel = z.infer<typeof BillingSubscriptionCancelSchema>;

export const BillingRefundCreateSchema = z.strictObject({
  /** Null refunds whatever remains of the amount paid. */
  amountMinor: PositiveMinorAmountSchema.nullable().default(null),
  reason: BillingReasonSchema,
});
export type BillingRefundCreate = z.infer<typeof BillingRefundCreateSchema>;

export const BillingInvoiceResponseSchema = z.strictObject({ invoice: BillingInvoiceSchema });
export const BillingSubscriptionResponseSchema = z.strictObject({
  subscription: BillingSubscriptionSchema,
});
export const BillingRefundResponseSchema = z.strictObject({ refund: BillingRefundSchema });
export const BillingCreditGrantResponseSchema = z.strictObject({
  credit: BillingCreditGrantSchema,
});
export const BillingCustomerResponseSchema = z.strictObject({ customer: BillingCustomerSchema });

export const BillingReconcileResponseSchema = z.strictObject({
  invoices: z.number().int().min(0),
  subscriptions: z.number().int().min(0),
  refunds: z.number().int().min(0),
  changed: z.number().int().min(0),
});
export type BillingReconcileResponse = z.infer<typeof BillingReconcileResponseSchema>;
